import { useCallback, useEffect, useMemo, useState } from 'react'

import { CampaignsService, FeatureFlagName } from '@api'
import { useUnit } from 'effector-react'
import { usePersistentState } from 'react-persistent-state'
import { useQuery } from 'react-query'

import { isCUI } from '@/consts/routes'
import { $isLoggedIn } from '@/models/account'
import { $creatorId } from '@/models/creators.ts'
import { $openedDialogs, hideDialog, openDialog } from '@/models/dialogs.ts'
import { $availableFeatures } from '@/models/feature-flags'
import { isFeatureAvailable } from '@/utils/featureFlags'
import { EVENTS, useCustomEventListener } from '@/utils/useEventListener'

import {
  CampaignsContext,
  SEEN_PROMOTION_DIALOGS_STORAGE_KEY,
  campaignEndDatesMap,
  discountsMap,
  promotionDialogsMap,
} from './consts'
import type { Campaigns } from './types'

type Props = React.PropsWithChildren

export const CampaignsProvider = ({ children }: Props) => {
  const creatorId = useUnit($creatorId)
  const isLoggedIn = useUnit($isLoggedIn)
  const [currentTime, setCurrentTime] = useState(Date.now())
  const availableFeatures = useUnit($availableFeatures)
  const isOngoingCampaignFeatureEnabled = isFeatureAvailable(
    availableFeatures,
    FeatureFlagName.ONGOING_CAMPAIGN_FEATURE,
    false,
  )

  const { data = [] } = useQuery(
    ['activeCampaigns', creatorId, isLoggedIn, isOngoingCampaignFeatureEnabled],
    () => CampaignsService.getActiveCampaigns({ creatorId: creatorId! }),
    { enabled: !!creatorId && !!isLoggedIn && isOngoingCampaignFeatureEnabled },
  )

  const activeCampaigns = useMemo(() => {
    return data.filter((campaign) => {
      const endDate = campaignEndDatesMap[campaign as Campaigns]

      return !endDate || currentTime < endDate
    }) as Campaigns[]
  }, [data, currentTime])

  useEffect(() => {
    const now = Date.now()

    // Find the nearest expiration and all campaigns expiring at that time
    const result = activeCampaigns.reduce<{
      nextExpiration: number | null
      expiringCampaigns: Campaigns[]
    }>(
      (acc, campaign) => {
        const endDate = campaignEndDatesMap[campaign]

        if (!endDate || endDate <= now) return acc

        if (acc.nextExpiration === null || endDate < acc.nextExpiration) {
          return {
            nextExpiration: endDate,
            expiringCampaigns: [campaign],
          }
        }

        if (endDate === acc.nextExpiration) {
          return {
            ...acc,
            expiringCampaigns: [...acc.expiringCampaigns, campaign],
          }
        }

        return acc
      },
      { nextExpiration: null, expiringCampaigns: [] },
    )

    if (result.nextExpiration) {
      const timeUntilExpiration = result.nextExpiration - now

      const timeout = setTimeout(() => {
        // Close promotion dialogs for campaigns that just expired
        // eslint-disable-next-line effector/no-getState
        const currentOpenedDialogs = $openedDialogs.getState()

        result.expiringCampaigns.forEach((campaign) => {
          const promotionDialogType = promotionDialogsMap[campaign]
          const openDialogs = currentOpenedDialogs.filter(
            (dialog) => dialog.type === promotionDialogType && dialog.isOpen,
          )

          openDialogs.forEach((dialog) => {
            hideDialog(dialog.id)
          })
        })

        setCurrentTime(Date.now())
      }, timeUntilExpiration)

      return () => clearTimeout(timeout)
    }
  }, [activeCampaigns])

  const activeCampaign = activeCampaigns.at(0)
  const [seenPromotionDialogs, setSeenPromotionDialogs] = usePersistentState<
    Partial<Record<Campaigns, boolean>>
  >(
    {},
    {
      storageKey: SEEN_PROMOTION_DIALOGS_STORAGE_KEY,
    },
  )

  const currentDiscount = useMemo(() => {
    if (!activeCampaign || !discountsMap[activeCampaign]) return 0

    return discountsMap[activeCampaign]
  }, [activeCampaign])

  const applyDiscount = useCallback(
    (price: number) => price - price * (currentDiscount / 100),
    [currentDiscount],
  )

  const markPromotionDialogAsSeen = useCallback(
    (campaign: Campaigns) => {
      setSeenPromotionDialogs((prev) => ({
        ...prev,
        [campaign]: true,
      }))
    },
    [setSeenPromotionDialogs],
  )

  useEffect(() => {
    if (activeCampaign && !isCUI) {
      const isPromotionDialogSeen = seenPromotionDialogs[activeCampaign]

      if (!isPromotionDialogSeen) {
        const promotionDialog = promotionDialogsMap[activeCampaign]

        openDialog({ type: promotionDialog })
        markPromotionDialogAsSeen(activeCampaign)
      }
    }
  }, [activeCampaign, seenPromotionDialogs, markPromotionDialogAsSeen])

  useCustomEventListener(EVENTS.LOGOUT, () =>
    localStorage.removeItem(SEEN_PROMOTION_DIALOGS_STORAGE_KEY),
  )

  const value = useMemo(
    () => ({
      activeCampaign,
      currentDiscount,
      applyDiscount,
    }),
    [activeCampaign, applyDiscount, currentDiscount],
  )

  return (
    <CampaignsContext.Provider value={value}>
      {children}
    </CampaignsContext.Provider>
  )
}
